import { Box, IconButton } from '@mui/material'
import { keyframes, styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'

import { BLUE_BASE } from '../../theme/variables/colors'
import { hexToRgba } from '../../utils/functions'
import CognitoLoginForm from '../Form/CognitoLoginForm'
import CognitoRegisterForm from '../Form/CognitoRegisterForm'
import EmailForm from '../Form/EmailForm'
import { useAnalytics } from '../../utils/analytics/useAnalytics'

const navyColor = hexToRgba(BLUE_BASE, 0.8)

const rotate = keyframes`
    from {
        transform: rotate(0deg);
    }
    to {
        transform: rotate(360deg);
    }
`

const OuterModal = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'emailForm',
})(({ theme, emailForm }) => ({
  margin: '0 auto',
  maxWidth: '100%',
  maxHeight: '100vh',
  height: '100%',
  width: '100%',
  padding: '20px',
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 9999,
  overflow: 'hidden',
  backgroundColor: emailForm ? 'transparent' : 'rgba(9, 42, 72, 0.95)',
}))

const InnerModal = styled(Box)(({ theme }) => ({
  margin: '0 auto',
  maxWidth: '600px',
  maxHeight: '90%',
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  height: 'fit-content',
  width: '100% !important',
  padding: '20px',
  overflow: 'hidden',
  backgroundColor: 'rgb(255, 255, 255)',
  boxShadow: `0 4px 8px ${navyColor}`,
  borderRadius: '20px',
}))

const HeaderModal = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  width: '100%',
  height: '40px',
}))

const BodyModal = styled(Box)(({ theme }) => ({
  overflowY: 'auto',
  overflowX: 'hidden',
  backgroundColor: '#FFFFFF',
  maxHeight: '75vh',
  height: 'fit-content',
  width: '100%',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
    maxHeight: '90vh',
  },
}))

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'relative',
  width: '30px',
  height: '30px',
  color: navyColor,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  cursor: 'pointer',
  transition: 'transform 0.4s ease-in-out',
  '&.spin': {
    animation: `${rotate} 0.4s ease-in-out`,
  },
}))

const Line = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '100%',
  height: '4px',
  backgroundColor: 'black',
  transition: 'transform 0.4s ease-in-out',
}))

const LineBefore = styled(Line)(({ theme }) => ({
  transform: 'rotate(45deg)',
}))

const LineAfter = styled(Line)(({ theme }) => ({
  transform: 'rotate(-45deg)',
}))

const HiddenTitle = styled('h2')({
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
})

const FormModalComponent = ({
  onClose,
  isOpen,
  formType,
  emailId,
  customValidation = null,
  isModal = true,
  ctaText = 'Submit',
  dataLocation = '',
  emailForm = false,
  ...props
}) => {
  const [isSpinning, setIsSpinning] = useState(false)
  const { track } = useAnalytics({
    component: { name: 'FormModal', description: 'Modal form container' },
    content: {
      formType,
      emailId,
      dataLocation: typeof dataLocation === 'string' ? dataLocation : undefined,
    },
  })
  const openRef = useRef(false)

  useEffect(() => {
    if (isOpen && !openRef.current) {
      track('modal_open', {
        interaction: { type: 'open' },
      })
    }
    openRef.current = isOpen
  }, [isOpen, track])

  const handleCloseClick = useCallback((source = 'close_button') => {
    setIsSpinning(true)
    track('modal_close', {
      interaction: { type: source },
    })
    setTimeout(() => {
      setIsSpinning(false)
      onClose()
    }, 400)
  }, [onClose, track])

  const handleLoginSuccess = useCallback((options) => {
    track('modal_close', {
      interaction: { type: 'success' },
    })
    onClose?.({ reason: 'success', ...options })
  }, [onClose, track])
  const handleEmailSubmit = useCallback(() => {
    handleCloseClick('form_submit')
  }, [handleCloseClick])

  if (!isOpen) {
    return null
  }

  if (typeof document === 'undefined') {
    console.warn('[FormModalComponent]: Attempted to render modal during SSR.')
    return null
  }

  const modalContainer = document.getElementById('modal-root') || document.body

  return ReactDOM.createPortal(
    <OuterModal
      role="dialog"
      aria-modal="true"
      aria-labelledby="form-modal-title"
      emailForm={emailForm}
      {...props}
    >
      <InnerModal>
        <HiddenTitle id="form-modal-title">Form Modal</HiddenTitle>
        <HeaderModal>
          <CloseButton
            onClick={handleCloseClick}
            className={isSpinning ? 'spin' : ''}
            aria-label="Close modal"
          >
            <LineBefore />
            <LineAfter />
          </CloseButton>
        </HeaderModal>
        <BodyModal>
          {formType === 'login' ? (
            <CognitoLoginForm onSuccess={handleLoginSuccess} />
          ) : formType === 'register' ? (
            <CognitoRegisterForm onSuccess={onClose} />
          ) : emailForm ? (
            <EmailForm
              emailId={emailId}
              formId={formType}
              isModal={isModal}
              ctaText={ctaText}
              customValidation={customValidation}
              dataLocation={dataLocation}
              onSubmit={handleEmailSubmit}
            />
          ) : null}
        </BodyModal>
      </InnerModal>
    </OuterModal>,
    modalContainer
  )
}

FormModalComponent.propTypes = {
  onClose: PropTypes.func,
  isOpen: PropTypes.bool,
  formId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  customValidation: PropTypes.any,
  emailForm: PropTypes.bool,
  isModal: PropTypes.bool,
  ctaText: PropTypes.string,
  dataLocation: PropTypes.any,
}

FormModalComponent.displayName = 'FormModalComponent'

const FormModal = React.memo(FormModalComponent)

export default FormModal
